import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { format } from "date-fns";
import { ReactionAndFavorite } from "../../reaction-and-favorite/reaction-and-favorite";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CustomMarkdown } from "../../../../custom-markdown/custom-markdown";
import { useAppStore } from "../../../../../stores/appStore";
import { TextMessageProps } from "../text-message";
import { useTextMessageStyles } from "../styles";
import Lottie from "lottie-react";

import {
  animationScore1Mob,
  animationScore2Mob,
  animationScore3Mob,
  animationScore4Mob,
  animationScore5Mob,
  animationScore1Desk,
  animationScore2Desk,
  animationScore3Desk,
  animationScore4Desk,
  animationScore5Desk,
} from "../../../../../assets/lottie";

const FEEDBACK_ANIMATION_KEY = "feedback_animation_shown";
const FEEDBACK_CONTENT_EXPANDED_KEY = "feedback_content_expanded";
const FEEDBACK_COLLAPSED_MAX_HEIGHT = 190;
const FEEDBACK_EXPANDED_HEIGHT_BUFFER = 36;
// Min height for embedded (tab) feedback while Lottie plays so the robot is not clipped.
const EMBEDDED_LOTTIE_MIN_HEIGHT_MOBILE = 280;
const EMBEDDED_LOTTIE_MIN_HEIGHT_DESKTOP = 340;

export const FeedbackMessage: React.FC<
  Omit<TextMessageProps, "nextMessage" | "setLargeHigth"> & {
    embedded?: boolean;
    animationKey?: string;
  }
> = ({
  message,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
  embedded = false,
  animationKey,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { setFeedBackAnimationFinish } = useAppStore();
  const styles = useTextMessageStyles(true, "default");
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const handleToggleCollapse = () => {
    setIsExpanded((wasExpanded) => {
      const next = !wasExpanded;
      localStorage.setItem(FEEDBACK_CONTENT_EXPANDED_KEY, next ? "1" : "0");
      return next;
    });
  };

  const [showLottie, setShowLottie] = useState<boolean>(false);
  const [score, setScore] = useState<number | undefined>(undefined);

  const mobileAnimationMapping: Record<number, any> = {
    0: animationScore1Mob,
    1: animationScore1Mob,
    2: animationScore2Mob,
    3: animationScore3Mob,
    4: animationScore4Mob,
    5: animationScore5Mob,
  };

  const desktopAnimationMapping: Record<number, any> = {
    0: animationScore1Desk,
    1: animationScore1Desk,
    2: animationScore2Desk,
    3: animationScore3Desk,
    4: animationScore4Desk,
    5: animationScore5Desk,
  };

  useEffect(() => {
    if (message.score === undefined) return;
    const key = `${FEEDBACK_ANIMATION_KEY}_${message.message_id}_${animationKey ?? "standalone"}`;
    const alreadyShown = sessionStorage.getItem(key) === "1";
    setScore(message.score);
    if (!alreadyShown) {
      setShowLottie(true);
    }
  }, [message, animationKey]);

  useEffect(() => {
    const savedPreference = localStorage.getItem(FEEDBACK_CONTENT_EXPANDED_KEY);
    if (savedPreference === "0") {
      setIsExpanded(false);
      return;
    }
    if (savedPreference === "1") {
      setIsExpanded(true);
    }
  }, []);

  const text = message.text || "";
  const contentInnerRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  useLayoutEffect(() => {
    const node = contentInnerRef.current;
    if (!node) return;

    const measure = () => setContentHeight(node.scrollHeight);
    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, [text, isMobile]);

  const isLongText = contentHeight > FEEDBACK_COLLAPSED_MAX_HEIGHT + 8;

  const combinedTitle = message.title
    ? message.title.replace(
        /(FEETBACK)/,
        '<span style="padding-right: 1rem;">$1</span>',
      )
    : "";

  const defaultAnimationData = isMobile
    ? animationScore1Mob
    : animationScore1Desk;

  const animationData =
    typeof score === "number" && score >= 0 && score <= 5
      ? isMobile
        ? mobileAnimationMapping[score]
        : desktopAnimationMapping[score]
      : defaultAnimationData;

  const LottieOverlay =
    showLottie && typeof score === "number" ? (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          pointerEvents: "none",
          background: "transparent",
          overflow: "hidden",
        }}
      >
        <Box
          sx={
            embedded
              ? {
                  width: "100%",
                  height: "100%",
                }
              : {
                  height: "110vh",
                  ...(!isMobile && { transform: "scale(1.3)" }),
                }
          }
        >
          <Lottie
            animationData={animationData}
            loop={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "fill",
            }}
            onComplete={() => {
              setShowLottie(false);
              setFeedBackAnimationFinish(true);
              const key = `${FEEDBACK_ANIMATION_KEY}_${message.message_id}_${animationKey ?? "standalone"}`;
              sessionStorage.setItem(key, "1");
            }}
          />
        </Box>
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        ...styles.feedbackContainer(embedded),
        ...(showLottie &&
          embedded && {
            minHeight: isMobile
              ? EMBEDDED_LOTTIE_MIN_HEIGHT_MOBILE
              : EMBEDDED_LOTTIE_MIN_HEIGHT_DESKTOP,
          }),
      }}
    >
      {LottieOverlay}
      {message.title && (
        <Box sx={styles.feedbackHeader}>
          <CustomMarkdown children={combinedTitle} />
        </Box>
      )}
      {message.text && (
        <Box
          sx={styles.feedbackContent(
            isLongText,
            isExpanded,
            Math.max(
              contentHeight + FEEDBACK_EXPANDED_HEIGHT_BUFFER,
              FEEDBACK_COLLAPSED_MAX_HEIGHT,
            ),
            FEEDBACK_COLLAPSED_MAX_HEIGHT,
          )}
        >
          <Box ref={contentInnerRef} sx={styles.feedbackContentInner}>
            <CustomMarkdown children={text} />
          </Box>
        </Box>
      )}

      <Box sx={styles.feedbackFooter}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isLongText && (
            <Box onClick={handleToggleCollapse} sx={styles.feedbackToggle}>
              <Typography variant="body2" sx={styles.feedbackToggleText}>
                {isExpanded ? "Show less" : "Show more"}
              </Typography>
              <ExpandMoreRoundedIcon
                sx={styles.feedbackToggleIcon(isExpanded)}
              />
            </Box>
          )}
          {!embedded && (
            <ReactionAndFavorite
              currentReaction={currentReaction}
              markedIds={markedIds}
              isSaved={isSaved}
              isTags={isTags}
              handleRemoveTag={handleRemoveTag}
              handleReactionClick={handleReactionClick}
              handleAddToFavorite={handleAddToFavorite}
              reactionBg="#f3f1ff"
            />
          )}
        </Box>
        {!embedded && (
          <Typography variant="caption" sx={styles.timestamp}>
            <span>{format(new Date(message.date), "dd/MM/yy")}</span>
            <span style={{ margin: "0 5px" }} />
            <span>{format(new Date(message.date), "HH:mm")}</span>
          </Typography>
        )}
      </Box>
    </Box>
  );
};
